// NaturalLanguageQuery.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  IconButton,
  InputAdornment,
  Tooltip,
  Chip,
} from "@mui/material";
import CustomTextField from "components/Form/CustomTextField";
import FlexWrapper from "components/Wrapper/FlexWrapper";
import MDBox from "components/MDBox";
import {
  generateNaturalLanguageReport,
  refineNaturalLanguageQuery,
} from "api/reports/naturalLanguage";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useAppContextController } from "context/AppContext";

const NaturalLanguageQuery = ({
  isLoading,
  reportingType,
  selectedSubtables = [],
  setToastAlert,
  setChartTableSrc,
  setChartSrc,
  setTabValue,
  setTableQueryDetails,
  setChartQueryDetails,
  setTableReportData,
  setChartReportData,
  setValue,
  query,
  setQuery,
  originalQuery,
  setOriginalQuery,
  hasRun,
  setHasRun,
  availableColumns = [],
  tableColumns = [],
}) => {
  const { company } = useAppContextController();

  const [localLoading, setLocalLoading] = useState(false);
  const [generatedQuery, setGeneratedQuery] = useState("");
  const [error, setError] = useState(null);
  const [reportTitle, setReportTitle] = useState("");
  const [refinementSuggestions, setRefinementSuggestions] = useState([]);

  const isCompany = company?.companyType === "Company";

  // Common handler for processing report responses
  const handleReportResponse = (response) => {
    if (response && response.iframe_url) {
      setToastAlert({
        isShow: true,
        message: "Report generated successfully!",
        status: "success",
      });

      // Store the SQL query that was generated
      if (response.queryDetails && response.queryDetails.query) {
        setGeneratedQuery(response.queryDetails.query);
      }

      // Store the report title if available
      if (response.reportTitle) {
        setReportTitle(response.reportTitle);
      }

      // Determine if this is a chart or table response
      const isChart = response.reportData?.formType === "chart";

      // First set the form type based on the response type
      setValue("formType", isChart ? "chart" : "table");

      if (isChart) {
        // Update chart iframe src
        setChartSrc(response.iframe_url);

        // Update chart query details
        setChartQueryDetails(response.queryDetails);

        // Update chart report data
        setChartReportData(response.reportData);

        // Update form values with chart criteria if available
        if (response.reportData?.criteria) {
          Object.entries(response.reportData.criteria).forEach(([key, value]) => {
            setValue(key, value);
          });
          // If dateInfo is empty, reset chart date fields
          const dateInfo = response.reportData.criteria?.dateInfo;
          if (dateInfo && Object.keys(dateInfo).length === 0) {
            setValue("chartDateField", "");
            setValue("chartDateRange", "");
            setValue("chartStartDate", "");
            setValue("chartEndDate", "");
          }
        }

        // Switch to chart tab
        setTabValue("chart");
      } else {
        // Update table iframe src
        setChartTableSrc(response.iframe_url);

        // Update table query details
        setTableQueryDetails(response.queryDetails);

        // Update table report data
        setTableReportData(response.reportData);

        // Update form values with table criteria if available
        if (response.reportData?.criteria) {
          Object.entries(response.reportData.criteria).forEach(([key, value]) => {
            setValue(key, value);
          });
          // If dateInfo is empty, reset table date fields
          const dateInfo = response.reportData.criteria?.dateInfo;
          if (dateInfo && Object.keys(dateInfo).length === 0) {
            setValue("dateField", "");
            setValue("dateRange", "");
            setValue("startDate", "");
            setValue("endDate", "");
          }
        }

        // Switch to table tab
        setTabValue("table");
      }
    } else {
      setError({
        message: response.error || "Failed to generate report",
        details: response.details,
      });

      // Store refinement suggestions if available
      if (response.refinementSuggestions) {
        setRefinementSuggestions(response.refinementSuggestions);
      }

      setToastAlert({
        isShow: true,
        message: response.error || "Failed to generate report",
        status: "error",
      });
    }
  };

  // Handle errors from API calls
  const handleError = (err) => {
    let message = "An error occurred while processing your query.";
    let details = "";
    if (err?.response?.data?.error) {
      message = err.response.data.error;
    } else if (err?.message) {
      details = err.message;
    }
    if (err?.response?.data?.details) {
      details = err.response.data.details;
    }
    setError({
      message,
      details,
    });

    // Store refinement suggestions if available
    if (err?.response?.data?.refinementSuggestions) {
      setRefinementSuggestions(err.response.data.refinementSuggestions);
    }

    setToastAlert({
      isShow: true,
      message,
      status: "error",
    });
  };

  // Get button text based on state
  const getButtonText = () => {
    if (localLoading) {
      return <CircularProgress size={24} />;
    }

    return tableColumns.length > 0
      ? `Generate Report (${tableColumns.length} columns)`
      : "Generate Report";
  };

  // Handle submission of a new query
  const handleSubmit = async () => {
    if (!query.trim()) return;

    setLocalLoading(true);
    setError(null);
    setOriginalQuery(query); // Store the original query for potential refinement
    setHasRun(true);

    try {
      // Prepare the payload - only include columns if user has selected specific ones
      const payload = {
        query,
        tableName: reportingType,
      };

      // Only add columns if user has specifically selected them
      if (tableColumns.length > 0) {
        payload.columns = tableColumns;
      }

      // Call the API endpoint with the natural language query
      const response = await generateNaturalLanguageReport(payload);

      handleReportResponse(response);
    } catch (err) {
      handleError(err);
    } finally {
      setLocalLoading(false);
    }
  };

  // Handle refinement of an existing query
  const handleRefine = async () => {
    if (!query.trim()) return;

    setLocalLoading(true);
    setError(null);

    try {
      // Prepare the payload - only include columns if user has selected specific ones
      const payload = {
        originalQuery,
        tableName: reportingType,
      };

      // Only add columns if user has specifically selected them
      if (tableColumns.length > 0) {
        payload.columns = tableColumns;
      }

      // Call the refinement endpoint
      const response = await refineNaturalLanguageQuery(payload);

      // Update the query with the refined version from the backend
      if (response.refinedQuery) {
        setQuery(response.refinedQuery);
        setOriginalQuery(response.refinedQuery); // Optionally update originalQuery to the new refined one
        setHasRun(true);
      }

      // Optionally, show the explanation somewhere
      // setRefinementExplanation(response.explanation);
    } catch (err) {
      handleError(err);
    } finally {
      setLocalLoading(false);
    }
  };

  // Handle using a refinement suggestion
  const handleUseSuggestion = (suggestion) => {
    setQuery(suggestion);
    setOriginalQuery(""); // Clear original query when using a suggestion
    setHasRun(false);
  };

  // Sample query quick select
  const handleSampleQuery = (sampleQuery) => {
    setQuery(sampleQuery);
    setOriginalQuery(""); // Clear original query when selecting a new sample
    setHasRun(false);
  };

  const sampleQueries = isCompany
    ? [
        { id: "jobs-ny", text: "Show me a table of all jobs in New York" },
        { id: "jobs-status", text: "Create a pie chart of jobs by status" },
        { id: "applicants-pending", text: "List all applicants with pending status" },
        { id: "jobs-payrate", text: "Show me jobs with a pay rate above $20" },
      ]
    : [
        { id: "jobs-ny", text: "Show me a table of all jobs in New York" },
        { id: "jobs-status", text: "Create a pie chart of jobs by status" },
        { id: "active-venues", text: "Show active venues created in the last 30 days" },
        { id: "events-status", text: "Count of events by status for venue ABC" },
        { id: "applicants-pending", text: "List all applicants with pending status" },
        { id: "jobs-payrate", text: "Show me jobs with a pay rate above $20" },
      ];

  return (
    <Box width="100%">
      <Typography variant="h5" mb={2}>
        Ask a question about your data
      </Typography>

      {/* Column selection indicator */}
      {tableColumns.length > 0 ? (
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "primary.light",
            color: "primary.contrastText",
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            Selected Columns ({tableColumns.length}):
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {tableColumns.slice(0, 10).map((columnName) => {
              const columnInfo = availableColumns.find((col) => col.value === columnName);
              const displayName = columnInfo?.label || columnName;
              return (
                <Chip
                  key={columnName}
                  label={displayName}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ bgcolor: "rgba(255, 255, 255, 0.2)" }}
                />
              );
            })}
            {tableColumns.length > 10 && (
              <Chip
                label={`+${tableColumns.length - 10} more`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ bgcolor: "rgba(255, 255, 255, 0.2)" }}
              />
            )}
          </Box>
          <Typography variant="caption" sx={{ mt: 1, display: "block", opacity: 0.8 }}>
            The AI will prioritize these columns when generating your report
          </Typography>
        </Paper>
      ) : (
        availableColumns.length > 0 && (
          <Paper
            elevation={1}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "warning.light",
              color: "warning.contrastText",
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              💡 Tip: Select columns first for better results
            </Typography>
            <Typography variant="body2">
              Use the column checkboxes on the left to select specific columns you want to include
              in your report. This helps the AI generate more accurate and focused results.
            </Typography>
          </Paper>
        )
      )}

      {/* Query input with refinement indicator */}
      <Paper
        elevation={2}
        sx={{
          p: 1,
          mb: 2,
          bgcolor: hasRun ? "rgba(25, 118, 210, 0.05)" : "inherit",
          border: hasRun ? "1px solid rgba(25, 118, 210, 0.2)" : "none",
        }}
      >
        {hasRun && originalQuery && (
          <Box display="flex" alignItems="center" mb={1} px={1}>
            <EditIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
            <Typography variant="caption" color="primary" fontWeight="medium">
              {query !== originalQuery
                ? "Refining your question"
                : "Edit your question for better results"}
            </Typography>
          </Box>
        )}

        <CustomTextField
          multiline
          rows={4}
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            tableColumns.length > 0
              ? "Example: Show me a pie chart of jobs by city (using your selected columns)"
              : "Example: Show me a pie chart of jobs by city"
          }
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: query && hasRun && (
              <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 2, mr: 1 }}>
                <Tooltip title="Refine query">
                  <IconButton
                    edge="end"
                    color="primary"
                    onClick={handleRefine}
                    disabled={localLoading}
                    size="small"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Action buttons */}
      <FlexWrapper justifyContent="space-between" mb={2}>
        {reportTitle && (
          <Chip
            label={reportTitle}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: "medium" }}
          />
        )}

        <Box>
          <Button
            variant="contained"
            color="primary"
            disabled={!query.trim() || isLoading || localLoading}
            onClick={handleSubmit}
            sx={{ minWidth: 150 }}
          >
            {getButtonText()}
          </Button>
        </Box>
      </FlexWrapper>

      {/* Error display with refinement suggestions */}
      {error && (
        <Paper
          elevation={1}
          sx={{ p: 2, mb: 3, bgcolor: "error.light", color: "error.contrastText" }}
        >
          <Box display="flex" alignItems="flex-start" mb={1}>
            <ErrorOutlineIcon sx={{ mr: 1, mt: 0.2 }} />
            <Typography variant="subtitle2">{error.message}</Typography>
          </Box>

          {error.details && (
            <Typography variant="body2" sx={{ mt: 1, ml: 4 }}>
              {error.details}
            </Typography>
          )}

          {refinementSuggestions?.length > 0 && (
            <Box mt={2} ml={4}>
              <Typography variant="body2" fontWeight="medium" color="error.contrastText" mb={1}>
                Here are some suggestions to help you refine your question:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {refinementSuggestions.map((suggestion) => (
                  <li
                    key={`suggestion-${suggestion.substring(0, 20).replace(/\s+/g, "-")}`}
                    style={{ marginBottom: 4 }}
                  >
                    <Typography variant="body2" color="error.contrastText">
                      {suggestion}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}
        </Paper>
      )}

      {/* SQL query display */}
      {generatedQuery && (
        <MDBox mt={3} p={2} border="1px solid" borderColor="grey.300" borderRadius={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            Generated SQL Query:
          </Typography>
          <Box
            component="pre"
            sx={{
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: 1,
              fontSize: "0.8rem",
            }}
          >
            {generatedQuery}
          </Box>
        </MDBox>
      )}

      {/* Example queries */}
      <MDBox mt={3}>
        <Box display="flex" alignItems="center" mb={1}>
          <LightbulbIcon color="info" fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="subtitle2" fontWeight="bold">
            {tableColumns.length > 0
              ? "Try asking questions about your selected columns:"
              : "Try asking questions like:"}
          </Typography>
        </Box>

        <Grid container spacing={1}>
          {sampleQueries?.map((example) => (
            <Grid item xs={12} sm={6} key={example.id}>
              <Button
                variant="text"
                color="primary"
                size="small"
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  textTransform: "none",
                  px: 1,
                }}
                onClick={() => handleSampleQuery(example.text)}
              >
                • {example.text}
              </Button>
            </Grid>
          ))}
        </Grid>

        {tableColumns.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            The AI will focus on your selected columns when generating reports
          </Typography>
        )}
      </MDBox>
    </Box>
  );
};

export default NaturalLanguageQuery;
